import {
  Badge,
  Box,
  Flex,
  HStack,
  Spinner,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import {
  useGetPendingInvitationsQuery,
  useAcceptInvitationMutation,
  useDeclineInvitationMutation,
} from '../../generated/graphql';
import { useAuth } from '../../contexts/AuthContext';

const getRoleBadgeColor = (role: string) => {
  switch (role) {
    case 'OWNER':
      return 'purple';
    case 'ADMIN':
      return 'blue';
    default:
      return 'gray';
  }
};

const formatDate = (date: string | Date) => {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const PendingInvitations = () => {
  const toast = useToast();
  const { refreshBackendUser } = useAuth();
  const { data, loading, refetch } = useGetPendingInvitationsQuery();
  const [acceptInvitation, { loading: accepting }] = useAcceptInvitationMutation();
  const [declineInvitation, { loading: declining }] = useDeclineInvitationMutation();

  const handleAccept = async (token: string) => {
    try {
      await acceptInvitation({
        variables: { token },
      });
      await refreshBackendUser();
      toast({
        title: 'Invitation accepted',
        description: 'You have joined the organization',
        status: 'success',
        duration: 3000,
      });
      refetch();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to accept invitation',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleDecline = async (token: string) => {
    try {
      await declineInvitation({
        variables: { token },
      });
      toast({
        title: 'Invitation declined',
        status: 'info',
        duration: 2000,
      });
      refetch();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to decline invitation',
        status: 'error',
        duration: 3000,
      });
    }
  };

  if (loading) {
    return (
      <Flex justify="center" py={8}>
        <Spinner color={HelivanColor.text.helivanBlue} />
      </Flex>
    );
  }

  const invitations = data?.getPendingInvitations || [];

  return (
    <VStack spacing={4} align="start" width="full">
      {invitations.length === 0 ? (
        <Box py={8} width="full" textAlign="center">
          <HelivanText variant="smallBody">
            You have no pending invitations.
          </HelivanText>
        </Box>
      ) : (
        <Box width="full" overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead bg={HelivanColor.background.lightestGrey}>
              <Tr>
                <Th borderColor={HelivanColor.border.primary}>
                  <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>
                    Organization
                  </HelivanText>
                </Th>
                <Th borderColor={HelivanColor.border.primary}>
                  <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>
                    Invited By
                  </HelivanText>
                </Th>
                <Th borderColor={HelivanColor.border.primary}>
                  <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>
                    Role
                  </HelivanText>
                </Th>
                <Th borderColor={HelivanColor.border.primary}>
                  <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>
                    Expires
                  </HelivanText>
                </Th>
                <Th borderColor={HelivanColor.border.primary}>
                  <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>
                    Actions
                  </HelivanText>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {invitations.map((invitation) => (
                <Tr key={invitation.id} _hover={{ bg: HelivanColor.background.lightestGrey }}>
                  <Td borderColor={HelivanColor.border.primary}>
                    <HelivanText
                      variant="smallBody"
                      colorOverride={HelivanColor.text.black}
                    >
                      {invitation.organization?.name}
                    </HelivanText>
                  </Td>
                  <Td borderColor={HelivanColor.border.primary}>
                    <HelivanText variant="smallBody">
                      {invitation.invitedByUser?.fullName}
                    </HelivanText>
                  </Td>
                  <Td borderColor={HelivanColor.border.primary}>
                    <Badge colorScheme={getRoleBadgeColor(invitation.role)} fontSize="10px">
                      {invitation.role}
                    </Badge>
                  </Td>
                  <Td borderColor={HelivanColor.border.primary}>
                    <HelivanText variant="smallBody">
                      {formatDate(invitation.expiresAt)}
                    </HelivanText>
                  </Td>
                  <Td borderColor={HelivanColor.border.primary}>
                    <HStack spacing={2}>
                      <HelivanButton
                        variant="green"
                        size="xs"
                        onClick={() => handleAccept(invitation.token!)}
                        isLoading={accepting}
                        isDisabled={accepting || declining}
                      >
                        Accept
                      </HelivanButton>
                      <HelivanButton
                        variant="ghost"
                        size="xs"
                        onClick={() => handleDecline(invitation.token!)}
                        isLoading={declining}
                        isDisabled={accepting || declining}
                      >
                        Decline
                      </HelivanButton>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </VStack>
  );
};
